import { GetSavedViewsList } from "@/data-access/saved-views/queries/saved-views-queries";
import { useQuery } from "urql";

interface SavedViewItem {
  id: string;
  name: string;
}

interface UseGetSavedViewsListReturn {
  items: SavedViewItem[];
  isLoading: boolean;
  error?: Error;
}

interface UseGetSavedViewsListProps {
  feedId?: string;
}

/**
 * Fetches a list of saved views for a specific feed.
 * Returns only `id` and `name` fields for use in dropdowns and selectors.
 * Query is paused if `feedId` is not provided.
 *
 * @example
 * ```tsx
 * const [selectedFeedId, setSelectedFeedId] = useState<string>();
 * const { items, isLoading } = useGetSavedViewsList({ feedId: selectedFeedId });
 *
 * Automatically refetches when selectedFeedId changes
 * <Select onChange={(e) => setSelectedFeedId(e.target.value)}>
 *   {items.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
 * </Select>
 * ```
 */
export const useGetSavedViewsList = ({
  feedId,
}: UseGetSavedViewsListProps): UseGetSavedViewsListReturn => {
  const [{ data, fetching: isLoading, error }] = useQuery({
    query: GetSavedViewsList,
    variables: { feedId: feedId as string },
    pause: !feedId,
  });

  const items = data?.feedSavedViews ?? [];

  return {
    items,
    isLoading,
    error,
  };
};
